// Cierre de la asesoría — Fase 4 de docs/PLAN-cotizador-guiado.md.
//
// Lógica PURA (sin React, sin red): qué hay que pedirle al cliente para cada
// producto, el compromiso del agente, el texto del consentimiento que firma el
// cliente, lo que falta antes de cerrar, y la cotización y las pólizas que se
// crean al cerrar con venta. La pantalla (components/CierreDeLaAsesoria.jsx)
// solo pinta y llama al servidor.
//
// LO QUE EL CLIENTE VE: sus datos, los productos, el precio, la fecha y el
// texto. NUNCA el semáforo de salud, la capacidad de pago ni el estatus
// migratorio — eso es de uso interno (el fallo que vimos en la de Entérate).

import { escaparHtml as escapar } from './enlaceDeCorreo.js';
import { TIPO_A_ENUM } from './policyTypes.js';
import { normalizarTipoDeSuplemento } from './tiposDeSuplemento.js';
import { coberturasDelPlan, noCubreDe, enlaceSeguro, esTipoPropio } from './cotizadorCatalogo.js';

/** Tope de folletos por opción (lo mismo que mira el servidor al firmarlos). */
export const MAX_FOLLETOS = 10;

/**
 * Los folletos del producto que puede abrir el CLIENTE (Lissette 2026-10-15:
 * "se traerá los brochures"). Los del catálogo están en el almacenamiento
 * privado: va la ruta (`blob`) y el servidor la firma al enseñar la cotización
 * solo si es de la base o de la misma agencia. Un enlace https público va tal
 * cual. El material SOLO para el agente (`materialAgente`) no sale nunca.
 */
export function folletosDelProducto(producto) {
  return (Array.isArray(producto?.brochures) ? producto.brochures : [])
    .map((b) => {
      const nombre = String(b?.nombre || 'Folleto').slice(0, 120);
      if (b?.blob) return { nombre, blob: String(b.blob) };
      const url = enlaceSeguro(b?.url);
      return url ? { nombre, url } : null;
    })
    .filter(Boolean)
    .slice(0, MAX_FOLLETOS);
}

/**
 * Lo que cubre y lo que no, para la opción de la COTIZACIÓN que recibe el
 * cliente (Lissette 2026-10-14: "sí, que salga en la cotización"). Va en
 * `coverageDetails` con dos claves propias que pintan la página del cliente,
 * el PDF y la vista del agente. Sin detalle en el catálogo, no se añade nada.
 */
export function detalleParaLaCotizacion(producto, plan) {
  const detalle = coberturasDelPlan(producto, plan);
  const noCubre = noCubreDe(producto);
  const out = {};
  if (detalle.length) out.detalleDeCobertura = detalle;
  if (noCubre.length) out.noCubre = noCubre;
  // Pagos fijos (hospital, accidente…): el monto es lo que el plan LE PAGA al
  // cliente, no lo que paga él. Sin decirlo, "Hospital $5,000" se lee como un
  // costo (el error nº 2 de la clase de UHC, MEMORY.md 2026-09-28).
  if (detalle.length && producto?.coberturas?.tipo === 'indemnizacion') out.lePaga = true;
  const folletos = folletosDelProducto(producto);
  if (folletos.length) out.folletos = folletos;
  return Object.keys(out).length ? out : null;
}

/**
 * Lo que pintan la página del cliente y la vista del agente a partir de
 * `coverageDetails`: un solo lector para que las dos digan lo mismo.
 */
export function leerDetalleDeCotizacion(coverageDetails) {
  const d = coverageDetails && typeof coverageDetails === 'object' ? coverageDetails : {};
  const filas = (Array.isArray(d.detalleDeCobertura) ? d.detalleDeCobertura : [])
    .filter((f) => f && f.concepto && f.valor)
    .map((f) => ({ concepto: String(f.concepto), valor: String(f.valor) }));
  const noCubre = (Array.isArray(d.noCubre) ? d.noCubre : []).map((x) => String(x ?? '').trim()).filter(Boolean);
  // En la página del cliente llegan con `url` (firmada por el servidor); en la
  // vista del agente, con `blob` o `url` tal como se guardaron.
  const folletos = (Array.isArray(d.folletos) ? d.folletos : [])
    .filter((f) => f && (f.url || f.blob))
    .map((f) => ({ nombre: String(f.nombre || 'Folleto'), ...(f.url ? { url: String(f.url) } : {}), ...(f.blob ? { blob: String(f.blob) } : {}) }));
  return { filas, noCubre, lePaga: d.lePaga === true, folletos };
}

/** El título del bloque: "Le paga a usted" en pagos fijos, "Lo que cubre" en lo demás. */
export const tituloDelDetalle = (lePaga, es) => (lePaga
  ? (es ? 'Le paga a usted (monto fijo)' : 'Pays you (fixed amount)')
  : (es ? 'Lo que cubre' : 'What it covers'));

/** Lo que el agente se compromete a haber hecho (texto propio de Deskte). */
export const COMPROMISO = [
  { id: 'explico', es: 'Le expliqué qué cubre y qué NO cubre cada producto, con sus esperas y límites.', en: 'I explained what each product covers and does NOT cover, including waiting periods and limits.' },
  { id: 'no_aca', es: 'Le dije que lo que no es del Mercado no es un seguro médico mayor ni cubre siempre las preexistencias.', en: 'I told them non-Marketplace products are not major medical and may not cover pre-existing conditions.' },
  { id: 'mercado', es: 'Le conté que el Mercado (ACA) existe, cuándo se puede inscribir y que ahí sí se cubren las preexistencias.', en: 'I told them about the Marketplace (ACA), when they can enroll, and that it covers pre-existing conditions.' },
  { id: 'precio', es: 'Revisamos juntos el total al mes y le dije que el precio final lo confirma la compañía.', en: 'We reviewed the monthly total together and I said the carrier confirms the final price.' },
  { id: 'decide', es: 'El cliente decidió sin presión y sabe cómo cancelar.', en: 'The client decided without pressure and knows how to cancel.' },
];

const esMercado = (l) => l?.categoria === 'aca' || l?.productoId === 'mercado';

/** Las líneas de la oferta que NO son del Mercado: las que llevan consentimiento. */
export const lineasNoAca = (oferta) => (oferta?.items || []).filter((l) => !esMercado(l));

/** Lo que hay que pedirle al cliente, por producto de la oferta (del catálogo). */
export function requisitosDeLaOferta(oferta, productos) {
  return (oferta?.items || []).map((l) => {
    const p = (productos || []).find((x) => x.id === l.productoId);
    const lista = (p?.requisitos || []).map((r) => String(r || '').trim()).filter(Boolean);
    return { productoId: l.productoId, nombre: l.nombre || p?.nombre || '', lista };
  }).filter((x) => x.lista.length);
}

/** Cuántos requisitos están marcados de cuántos hay. */
export function conteoDeRequisitos(oferta, productos, marcados) {
  let total = 0;
  let hechos = 0;
  for (const r of requisitosDeLaOferta(oferta, productos)) {
    total += r.lista.length;
    const m = (marcados || {})[r.productoId] || [];
    hechos += r.lista.filter((_, i) => m.includes(i)).length;
  }
  return { total, hechos };
}

/** Los folletos que el CLIENTE puede abrir: solo los que tienen un enlace https público. */
export function folletosPublicos(oferta, productos) {
  const out = [];
  for (const l of oferta?.items || []) {
    const p = (productos || []).find((x) => x.id === l.productoId);
    for (const b of p?.brochures || []) {
      const url = String(b?.url || '').trim();
      if (/^https:\/\//i.test(url)) out.push({ producto: l.nombre || p?.nombre || '', nombre: b.nombre || 'Folleto', url });
    }
  }
  return out;
}

const dolares = (n) => (n == null ? '' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/**
 * El CONSENTIMIENTO que firma el cliente cuando compra algo que no es del
 * Mercado. Texto PROPIO de Deskte (Lissette 2026-10-14: "lo redactas tú"),
 * pendiente de revisar con un abogado: por eso la pantalla deja editarlo antes
 * de enviarlo y lo dice.
 *
 * @param lineas  las líneas de la oferta con su `monto` (de totalDeLaOferta)
 * @returns { titulo, html } — html sencillo, con todo lo variable escapado
 */
export function textoDelConsentimiento({ nombre, lineas, total, agente, fecha, folletos = [], textoExtra = '' }, lang = 'es') {
  const es = lang !== 'en';
  // El párrafo propio de la agencia va al final, escapado como todo lo demás.
  const extra = String(textoExtra || '').trim();
  const propio = extra ? `<p>${escapar(extra).replace(/\n/g, '<br>')}</p>` : '';
  const fechaTxt = (fecha || new Date()).toLocaleDateString('en-US');
  const items = (lineas || []).map((l) => `<li><b>${escapar(l.nombre)}</b>${l.compania ? ` — ${escapar(l.compania)}` : ''}${l.plan ? ` (${escapar(l.plan)})` : ''}${l.monto != null ? `: ${dolares(l.monto)} ${es ? 'al mes' : 'per month'}` : ''}</li>`).join('');
  const listaFolletos = folletos.length
    ? `<p>${es ? 'Folletos de los productos:' : 'Product brochures:'}</p><ul>${folletos.map((f) => `<li><a href="${escapar(f.url)}">${escapar(f.producto)} — ${escapar(f.nombre)}</a></li>`).join('')}</ul>`
    : `<p>${es ? 'Puedo pedirle a mi agente los folletos de cada producto en cualquier momento.' : 'I can ask my agent for the brochure of each product at any time.'}</p>`;
  if (es) {
    return {
      titulo: 'Consentimiento: productos que no son un plan del Mercado de Salud',
      html: [
        `<p>Yo, <b>${escapar(nombre)}</b>, he decidido contratar:</p>`,
        `<ul>${items}</ul>`,
        total != null ? `<p>Total aproximado: <b>${dolares(total)} al mes</b>. El precio final lo confirma cada compañía al aprobar la solicitud.</p>` : '',
        '<p>Mi agente me explicó y entiendo que:</p>',
        '<ul>',
        '<li>Estos productos <b>no son un seguro médico mayor</b> ni un plan calificado del Mercado de Salud (ACA).</li>',
        '<li>Pueden <b>no cubrir condiciones preexistentes</b>, y tener periodos de espera, límites, exclusiones y preguntas de salud.</li>',
        '<li>Un plan de indemnización paga <b>cantidades fijas</b>, aunque el servicio cueste más; una membresía de acceso médico <b>no es un seguro</b>.</li>',
        '<li>Existe el Mercado de Salud, donde los planes cubren las condiciones preexistentes, y me puedo inscribir en el periodo abierto o con un evento de vida que me dé un periodo especial.</li>',
        '<li>La cobertura empieza cuando la compañía aprueba la solicitud y se hace el primer pago, y puedo cancelar según las reglas de cada compañía.</li>',
        '</ul>',
        listaFolletos,
        propio,
        `<p>Tomé esta decisión libremente${agente ? `, con la asesoría de <b>${escapar(agente)}</b>` : ''}.</p>`,
        `<p>Fecha: ${fechaTxt}</p>`,
      ].join(''),
    };
  }
  return {
    titulo: 'Consent: products that are not a Health Insurance Marketplace plan',
    html: [
      `<p>I, <b>${escapar(nombre)}</b>, have decided to purchase:</p>`,
      `<ul>${items}</ul>`,
      total != null ? `<p>Approximate total: <b>${dolares(total)} per month</b>. Each carrier confirms the final price when it approves the application.</p>` : '',
      '<p>My agent explained, and I understand, that:</p>',
      '<ul>',
      '<li>These products are <b>not major medical insurance</b> and are not a qualified Health Insurance Marketplace (ACA) plan.</li>',
      '<li>They may <b>not cover pre-existing conditions</b>, and may have waiting periods, limits, exclusions and health questions.</li>',
      '<li>A fixed indemnity plan pays <b>fixed amounts</b> even if the service costs more; a medical access membership <b>is not insurance</b>.</li>',
      '<li>The Health Insurance Marketplace exists, its plans cover pre-existing conditions, and I can enroll during open enrollment or with a qualifying life event.</li>',
      '<li>Coverage starts when the carrier approves the application and the first payment is made, and I can cancel under each carrier\'s rules.</li>',
      '</ul>',
      listaFolletos,
      propio,
      `<p>I made this decision freely${agente ? `, advised by <b>${escapar(agente)}</b>` : ''}.</p>`,
      `<p>Date: ${fechaTxt}</p>`,
    ].join(''),
  };
}

/**
 * Lo que falta antes de cerrar CON VENTA, en palabras de agente. No bloquea:
 * se avisa y el agente decide (en la de Entérate se podía cerrar con todo
 * vacío sin un solo aviso).
 */
export function avisosAntesDeCerrar({ oferta, totales, requisitos, compromiso, consentimiento, tieneCliente, catalogoOk = true, puedePolizas = true }, es = true) {
  const a = [];
  if (!catalogoOk) a.push(es ? 'No se pudo cargar el catálogo: los precios y los datos a pedir no se pueden comprobar.' : 'The catalog could not be loaded.');
  if (!(oferta?.items || []).length) a.push(es ? 'No hay ninguna opción elegida en el paso 4.' : 'No option chosen in step 4.');
  if (totales?.faltan) a.push(es ? `Faltan ${totales.faltan} precio(s) en la opción.` : `${totales.faltan} price(s) missing.`);
  if (requisitos?.total && requisitos.hechos < requisitos.total) a.push(es ? `Faltan ${requisitos.total - requisitos.hechos} de ${requisitos.total} datos por pedir.` : `${requisitos.total - requisitos.hechos} of ${requisitos.total} requirements missing.`);
  const marcados = COMPROMISO.filter((c) => (compromiso || {})[c.id]).length;
  if (marcados < COMPROMISO.length) a.push(es ? `Compromiso del agente: ${marcados} de ${COMPROMISO.length} marcados.` : `Agent checklist: ${marcados} of ${COMPROMISO.length}.`);
  if (lineasNoAca(oferta).length && consentimiento?.estado !== 'SIGNED') {
    a.push(consentimiento?.id
      ? (es ? 'El cliente todavía no ha firmado el consentimiento.' : 'The client has not signed the consent yet.')
      : (es ? 'No se le ha enviado el consentimiento al cliente.' : 'The consent has not been sent.'));
  }
  if (!tieneCliente) a.push(es ? 'No es un cliente del CRM: se crea la cotización, pero las pólizas no (conviértalo en cliente primero).' : 'Not a CRM client: the quote is created but not the policies.');
  else if (!puedePolizas) a.push(es ? 'Su usuario no tiene permiso para crear pólizas: se crea solo la cotización.' : 'No permission to create policies: only the quote is created.');
  return a;
}

// La categoría del catálogo → el ramo de la póliza (crm-app/policyTypes.js) y
// el tipo de plan de la cotización (QuoteItem.planType).
const RAMO = {
  aca: 'aca', short_term: 'shortterm', indemnity: 'suppl', hospital: 'hosp', accidente: 'accident',
  acceso: 'referred', dental: 'dental', vision: 'vision', vida: 'life', asistencia: 'suppl', otro: 'suppl',
};
// El subtipo de la póliza Suplementaria: uno de los DE FÁBRICA, para que el
// informe por tipo las junte con las que vienen de una cotización convertida
// (deskte-api/src/utils/ramoDesdeCotizacion.ts usa este mismo).
const SUPLEMENTO = { indemnity: normalizarTipoDeSuplemento('Hospitalización / Indemnización'), asistencia: normalizarTipoDeSuplemento('Hospitalización / Indemnización') };

// Un TIPO DE PÓLIZA PROPIO de la agencia (custom_…) es su propio ramo: la póliza
// se crea de ese tipo y la cotización lo lleva tal cual (el servidor lo guarda
// como HEALTH con su id sellado, igual que Pólizas: tiposDePoliza.ts).
export const ramoDePoliza = (categoria) => (esTipoPropio(categoria) ? categoria : (RAMO[categoria] || 'suppl'));
/** El tipo de plan de la cotización: el enum del ramo, con la tabla de siempre (policyTypes.js). */
export const tipoDePlan = (categoria) => (esTipoPropio(categoria) ? categoria : (TIPO_A_ENUM[ramoDePoliza(categoria)] || 'HEALTH'));

/** Los topes de la póliza en el servidor (PolicyCreateSchema): el catálogo admite más. */
export const MAX_COMPANIA_POLIZA = 120;
export const MAX_PLAN_POLIZA = 160;

/**
 * La clave con que se apunta cada póliza creada: el producto y su plan, NO la
 * posición — si la oferta cambia entre dos intentos, la posición se mueve y se
 * duplicaría o se saltaría una.
 */
export const claveDePoliza = (l) => `${l.productoId}|${l.plan || ''}`;

/** El primer día del mes que viene, en el día LOCAL (YYYY-MM-DD). */
export function primeroDelMesQueViene(hoy = new Date()) {
  const d = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/** La cotización que se crea al cerrar con venta (la forma de POST /quotes). */
export function cotizacionDelCierre({ caso, lineas, es = true }) {
  const quien = caso?.nombre || (es ? 'Cliente' : 'Client');
  return {
    title: `${es ? 'Asesoría' : 'Consultation'} — ${quien}`.slice(0, 200),
    clientId: caso?.clientId || undefined,
    leadId: caso?.clientId ? undefined : (caso?.leadId || undefined),
    // Sin cliente el servidor pide un nombre de destinatario (un lead también).
    recipientName: caso?.clientId ? undefined : quien,
    notes: es ? 'Creada al cerrar la asesoría guiada.' : 'Created when the guided consultation was closed.',
    items: (lineas || []).map((l) => {
      // Del catálogo, su detalle; del Mercado (cotizado ahí mismo), lo que trajo
      // el cotizador (deducible, máximo de bolsillo…).
      const detalle = l.producto ? detalleParaLaCotizacion(l.producto, l.plan)
        : (l.coverage && typeof l.coverage === 'object' ? l.coverage : null);
      // Si la línea es solo para algunas personas, se dice (Lissette 2026-10-15).
      const hogar = caso?.datos?.hogar || [];
      const quienes = Array.isArray(l.para) && l.para.length && l.para.length < hogar.filter((m) => m?.quiereCobertura !== false).length
        ? l.para.map((i) => String(hogar[i]?.nombre || '').trim().split(/\s+/)[0]).filter(Boolean)
        : [];
      const base = [l.nombre, l.plan].filter(Boolean).join(' — ') || (es ? 'Producto' : 'Product');
      return {
        description: (quienes.length ? `${base} (${es ? 'para' : 'for'} ${quienes.join(', ')})` : base).slice(0, 300),
        carrier: l.compania || '',
        planType: tipoDePlan(l.categoria),
        quantity: 1,
        unitPrice: l.monto != null ? String(l.monto) : '0',
        ...(detalle ? { coverageDetails: detalle } : {}),
      };
    }),
  };
}

/** Una póliza PENDIENTE por producto (la forma de createPolicy en api.js). */
export function polizasDelCierre({ caso, lineas, efectiva }) {
  if (!caso?.clientId) return [];
  return (lineas || []).map((l) => {
    const ramo = ramoDePoliza(l.categoria);
    const p = {
      clientId: caso.clientId,
      type: ramo,
      carrier: (l.compania || 'Unknown').slice(0, MAX_COMPANIA_POLIZA),
      plan: [l.nombre, l.plan].filter(Boolean).join(' — ').slice(0, MAX_PLAN_POLIZA),
      monthlyPremium: l.monto != null ? String(l.monto) : '0',
      effectiveDate: efectiva,
      status: 'pending',
      notes: 'Creada al cerrar la asesoría guiada del cotizador.',
    };
    if (ramo === 'suppl' && SUPLEMENTO[l.categoria]) p.supplementType = SUPLEMENTO[l.categoria];
    return { clave: claveDePoliza(l), productoId: l.productoId, poliza: p };
  });
}

// ── El informe (interno) ─────────────────────────────────────────────────────
//
// Para el expediente de la agencia: CONFIDENCIAL, sí lleva el estatus y el
// semáforo (es interno). Solo estilos en el atributo `style`: la ventana de
// impresión hereda la CSP de la app, que bloquea los bloques <style> (ver
// printClientFicha en ClientDetail.jsx).
/** Una fecha 'YYYY-MM-DD' o ISO, en el día LOCAL y en formato en-US. */
function fechaLocal(v) {
  if (!v) return '';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? new Date(`${v}T12:00:00`) : new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US');
}
export { fechaLocal };

export function informeDeLaAsesoria({ caso, lineas, total, etiquetas, requisitos, marcados, compromiso, consentimiento, es = true }) {
  const h = escapar;
  const d = caso?.datos || {};
  const S = {
    sec: 'margin:14px 0 6px;font-size:12pt;font-weight:700;color:#0f172a;border-bottom:1px solid #cbd5e1;padding-bottom:3px',
    td: 'padding:4px 6px;border-bottom:1px solid #e2e8f0;font-size:10pt;vertical-align:top',
    th: 'padding:4px 6px;border-bottom:1px solid #94a3b8;font-size:9pt;text-align:left;color:#475569',
    tabla: 'width:100%;border-collapse:collapse',
  };
  const fila = (cols) => `<tr>${cols.map((c) => `<td style="${S.td}">${c}</td>`).join('')}</tr>`;
  const enc = (cols) => `<tr>${cols.map((c) => `<th style="${S.th}">${c}</th>`).join('')}</tr>`;
  const hogar = (d.hogar || []).map((m) => fila([
    // La edad también se escapa: el servidor guarda `datos` tal cual y no exige un número.
    h(m.nombre || '—'), h(m.relacion || ''), h(m.edad ?? '—'), h(etiquetas.estatus(m.estatus) || '—'), m.quiereCobertura === false ? (es ? 'No' : 'No') : (es ? 'Sí' : 'Yes'),
  ])).join('');
  const oferta = (lineas || []).map((l) => fila([h(l.nombre), h(l.compania || ''), h(l.plan || ''), l.monto != null ? dolares(l.monto) : (es ? 'sin precio' : 'no price')])).join('');
  const reqs = (requisitos || []).map((r) => `<div style="margin:4px 0"><b>${h(r.nombre)}</b><ul style="margin:2px 0 0 18px;padding:0">${r.lista.map((t, i) => `<li>${((marcados || {})[r.productoId] || []).includes(i) ? '☑' : '☐'} ${h(t)}</li>`).join('')}</ul></div>`).join('');
  const comp = COMPROMISO.map((c) => `<li>${(compromiso || {})[c.id] ? '☑' : '☐'} ${h(es ? c.es : c.en)}</li>`).join('');
  const firma = !consentimiento?.id
    ? (lineasNoAca({ items: lineas }).length ? (es ? 'No enviado' : 'Not sent') : (es ? 'No hace falta (solo Mercado)' : 'Not needed (Marketplace only)'))
    : consentimiento.estado === 'SIGNED'
      ? `${es ? 'Firmado' : 'Signed'}${consentimiento.firmadoEl ? ` ${h(fechaLocal(consentimiento.firmadoEl))}` : ''}`
      : (es ? 'Enviado, sin firmar todavía' : 'Sent, not signed yet');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${h(es ? 'Informe de la asesoría' : 'Consultation report')} — ${h(caso?.nombre || '')}</title></head>
<body style="font-family:Arial,Helvetica,sans-serif;color:#0f172a;margin:24px;font-size:10.5pt">
<div style="font-size:16pt;font-weight:800">${h(es ? 'Informe de la asesoría' : 'Consultation report')}</div>
<div style="margin:4px 0 10px;color:#334155"><b>${h(caso?.nombre || '—')}</b>${caso?.agenteNombre ? ` · ${es ? 'Agente' : 'Agent'}: ${h(caso.agenteNombre)}` : ''}${caso?.datos?.cierre?.cerradoEl ? ` · ${es ? 'Cerrada el' : 'Closed'} ${h(fechaLocal(caso.datos.cierre.cerradoEl))}` : ''} · ${es ? 'Impreso el' : 'Printed'} ${new Date().toLocaleDateString('en-US')}</div>
<div style="${S.sec}">1. ${es ? 'Resumen' : 'Summary'}</div>
<table style="${S.tabla}">${fila([`<b>${es ? 'Elegibilidad' : 'Eligibility'}</b>`, h(etiquetas.elegibilidad), `<b>${es ? 'Salud' : 'Health'}</b>`, h(etiquetas.salud), `<b>${es ? 'Capacidad' : 'Capacity'}</b>`, h(etiquetas.capacidad)])}</table>
<div style="${S.sec}">2. ${es ? 'Quién se cubre' : 'Household'}</div>
<table style="${S.tabla}">${enc([es ? 'Persona' : 'Person', es ? 'Relación' : 'Relation', es ? 'Edad' : 'Age', es ? 'Estatus' : 'Status', es ? 'Se cubre' : 'Covered'])}${hogar}</table>
<div style="${S.sec}">3. ${es ? 'La opción elegida' : 'Chosen option'}</div>
<table style="${S.tabla}">${enc([es ? 'Producto' : 'Product', es ? 'Compañía' : 'Carrier', 'Plan', es ? 'Al mes' : 'Monthly'])}${oferta}${fila(['', '', `<b>Total</b>`, `<b>${dolares(total || 0)}</b>`])}</table>
${reqs ? `<div style="${S.sec}">4. ${es ? 'Datos pedidos al cliente' : 'Requirements'}</div>${reqs}` : ''}
<div style="${S.sec}">5. ${es ? 'Compromiso del agente' : 'Agent checklist'}</div><ul style="margin:2px 0 0 18px;padding:0">${comp}</ul>
<div style="${S.sec}">6. ${es ? 'Consentimiento del cliente' : 'Client consent'}</div><div>${firma}</div>
<div style="margin-top:22px;padding-top:6px;border-top:1px solid #cbd5e1;font-size:8.5pt;color:#64748b">${h(es ? 'CONFIDENCIAL — solo para uso interno. El semáforo de salud, la capacidad de pago y el estatus migratorio nunca se le muestran al cliente.' : 'CONFIDENTIAL — internal use only.')}</div>
</body></html>`;
}
